import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Dna, ArrowRight, X, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "viralcut_dna_banner_dismissed";

/**
 * Conversion banner for users who haven't completed their Brand DNA yet.
 * Three states:
 *  - never started → strong CTA
 *  - started but not complete → progress nudge
 *  - complete → don't show (DnaCard shows instead)
 * Dismiss is stored in localStorage so it doesn't nag forever.
 */
export default function BrandDnaBanner() {
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | hidden | new | progress
  const [progress, setProgress] = useState({ msgs: 0 });

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === "1") { setState("hidden"); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/brand-dna/chat`);
        if (cancelled) return;
        if (data?.summary || data?.is_complete) { setState("hidden"); return; }
        const userMsgs = (data?.messages || []).filter(m => m.role === "user").length;
        setProgress({ msgs: userMsgs });
        setState(userMsgs === 0 ? "new" : "progress");
      } catch (_) {
        if (!cancelled) setState("hidden");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem(DISMISS_KEY, "1");
    setState("hidden");
  };

  if (state === "hidden" || state === "loading") return null;

  const isNew = state === "new";

  return (
    <button
      onClick={() => navigate("/brand-dna")}
      className="group relative w-full overflow-hidden rounded-2xl border border-cyan-500/30 hover:border-cyan-400/60 transition-all text-left p-4 sm:p-5 bg-gradient-to-br from-cyan-500/10 via-zinc-900/60 to-lime-500/10"
      data-testid={isNew ? "dna-banner-new" : "dna-banner-progress"}
    >
      {/* Glow accents */}
      <div className="absolute -top-12 -right-12 w-40 h-40 bg-cyan-400/20 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 w-40 h-40 blur-3xl rounded-full pointer-events-none" style={{ background: 'rgba(200,255,77,0.15)' }} />

      <div className="relative flex items-center gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/30 to-lime-400/20 border border-cyan-400/30 flex items-center justify-center">
          <Dna className="w-5 h-5 text-cyan-200" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-3 h-3 text-cyan-300" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-cyan-300">
              {isNew ? "Neu — exklusiv für Premium" : "Fast geschafft"}
            </span>
          </div>
          <p className="text-sm sm:text-base font-bold text-white mb-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isNew
              ? "Schalte deine Brand DNA frei"
              : `Mach weiter — du bist bei Nachricht ${progress.msgs}`}
          </p>
          <p className="text-[11px] sm:text-xs text-zinc-300 leading-relaxed">
            {isNew
              ? "Beantworte 2 Fragen — die KI baut dir Zielgruppe, Methode, Angebot & Preis. Alle Tools werden danach smarter."
              : "Komm zurück und schließ deine Brand-DNA-Session ab. Beim nächsten Tool-Start fließt sie automatisch ein."}
          </p>
        </div>

        <div className="flex-shrink-0 flex items-center gap-1">
          <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-cyan-200 group-hover:text-cyan-100 group-hover:translate-x-0.5 transition-all">
            {isNew ? "Jetzt starten" : "Weitermachen"}
            <ArrowRight className="w-3.5 h-3.5" />
          </span>
          <span
            onClick={dismiss}
            className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors cursor-pointer"
            title="Banner ausblenden"
            data-testid="dna-banner-dismiss"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && dismiss(e)}
          >
            <X className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </button>
  );
}
